import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';
import { API } from '../contexts/AuthContext';
import ChecklistAtendimento, { TabelaConsumo } from '../components/insumos/ChecklistAtendimento';
import EstoqueProdutos from '../components/insumos/EstoqueProdutos';
import ExtratoMovimentacoes from '../components/insumos/ExtratoMovimentacoes';
import PainelFerramentas from '../components/insumos/PainelFerramentas';
import { formatDateBR } from '../helpers/formatDate';
import { STATUS_CHECKLIST, TIPO_ATENDIMENTO_LABEL } from '../helpers/insumoChecklist';

const STATUS_ABERTOS = ['rascunho', 'em_separacao', 'pronto_para_saida', 'em_atendimento', 'aguardando_devolucao'];

function StatusPill({ status }) {
  const s = STATUS_CHECKLIST[status] || { label: status, pill: '' };
  return <span className={`status-pill ${s.pill}`}>{s.label}</span>;
}

// Extrato geral: carrega os catalogos usados nos filtros.
function AbaExtrato() {
  const [catalogos, setCatalogos] = useState({ categorias: [], marcas: [], tipos: [] });
  useEffect(() => {
    Promise.all([
      axios.get(`${API}/insumos/categorias`),
      axios.get(`${API}/insumos/marcas`),
      axios.get(`${API}/insumos/tipos-movimentacao`),
    ])
      .then(([c, m, t]) => setCatalogos({ categorias: c.data.data, marcas: m.data.data, tipos: t.data.data }))
      .catch(() => {});
  }, []);
  return <ExtratoMovimentacoes {...catalogos} />;
}

function AbaChecklists({ checklists, loading, onAbrir, onNovo, filtro, setFiltro }) {
  return (
    <section className="content-card">
      <div className="insumo-ck__secao-topo">
        <div className="vw-chips">
          {[['abertos', 'Em aberto'], ['todos', 'Todos']].map(([k, label]) => (
            <button key={k} type="button" className={`vw-chip ${filtro === k ? 'vw-chip--on' : 'vw-chip--off'}`} onClick={() => setFiltro(k)}>
              {label}
            </button>
          ))}
        </div>
        <button type="button" className="btn-portal btn-portal--primary" onClick={onNovo}>
          <i className="bi bi-plus-lg" /> Novo checklist
        </button>
      </div>
      {loading ? (
        <div className="content-card--centered">
          <div className="screen-loader__spinner" />
          <p>Carregando checklists...</p>
        </div>
      ) : checklists.length ? (
        <div className="table-shell">
          <table className="portal-table">
            <thead>
              <tr><th>#</th><th>Data</th><th>Tipo</th><th>Localidade</th><th>Responsável</th><th>Chamado</th><th>Materiais</th><th>Status</th><th /></tr>
            </thead>
            <tbody>
              {checklists.map((c) => (
                <tr key={c.id} onClick={() => onAbrir(c.id)} style={{ cursor: 'pointer' }}>
                  <td className="cell-muted">{c.id}</td>
                  <td>{formatDateBR(c.data_atendimento)}</td>
                  <td>{TIPO_ATENDIMENTO_LABEL[c.tipo_atendimento]}</td>
                  <td><span className="cell-strong">{c.localidade || '—'}</span></td>
                  <td className="cell-muted">{c.responsavel || '—'}</td>
                  <td className="cell-muted" title={c.chamado_titulo || ''}>{c.chamado_id ? `#${c.chamado_id}` : '—'}</td>
                  <td className="cell-muted">{c.itens.length}</td>
                  <td><StatusPill status={c.status} /></td>
                  <td>
                    <button type="button" className="btn-portal btn-portal--outline" onClick={(e) => { e.stopPropagation(); onAbrir(c.id); }}>
                      Abrir
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="empty-state">
          <strong>Nenhum checklist {filtro === 'abertos' ? 'em aberto' : 'registrado'}</strong>
          <span>Crie um checklist antes de sair para a loja: materiais, ferramentas e conferência de estoque.</span>
        </div>
      )}
    </section>
  );
}

function AbaHistorico({ checklists, loading, onAbrir }) {
  if (loading) {
    return (
      <section className="content-card content-card--centered">
        <div className="screen-loader__spinner" />
        <p>Carregando histórico...</p>
      </section>
    );
  }
  if (!checklists.length) {
    return (
      <section className="content-card">
        <div className="empty-state">
          <strong>Nenhum atendimento finalizado</strong>
          <span>Os checklists finalizados aparecem aqui com retirada, uso, devolução e consumo real.</span>
        </div>
      </section>
    );
  }
  return checklists.map((c) => (
    <section key={c.id} className="content-card">
      <div className="insumo-ck__secao-topo">
        <h3 className="insumo-ck__secao">
          {formatDateBR(c.data_atendimento)} — {c.localidade || '—'}
        </h3>
        <button type="button" className="btn-portal btn-portal--outline" onClick={() => onAbrir(c.id)}>
          Detalhes
        </button>
      </div>
      <p className="cell-muted" style={{ marginTop: 0 }}>
        <strong>Tipo:</strong> {TIPO_ATENDIMENTO_LABEL[c.tipo_atendimento]} · <strong>Responsável:</strong> {c.responsavel || '—'}
        {' '}· Checklist #{c.id}{c.chamado_id ? ` · Chamado #${c.chamado_id}` : ''}
      </p>
      <TabelaConsumo itens={c.itens} />
    </section>
  ));
}

export default function Insumos() {
  const [aba, setAba] = useState('checklists'); // checklists | ferramentas | estoque | extrato | historico
  const [erro, setErro] = useState('');
  const [catalogo, setCatalogo] = useState(null);
  const [checklists, setChecklists] = useState([]);
  const [historico, setHistorico] = useState([]);
  const [loadingChecklists, setLoadingChecklists] = useState(true);
  const [filtro, setFiltro] = useState('abertos');
  const [aberto, setAberto] = useState(null); // { id } | { id: null, chamado? } (novo)

  // Vindo da tela de Chamados: ?checklist=12 abre aquele checklist;
  // ?novo=1&chamado=345 abre um novo ja vinculado ao chamado.
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const checklist = searchParams.get('checklist');
    const novo = searchParams.get('novo');
    if (!checklist && !novo) return;
    setAba('checklists');
    setAberto(checklist ? { id: Number(checklist) } : { id: null, chamado: searchParams.get('chamado') || '' });
    setSearchParams({}, { replace: true });
  }, [searchParams, setSearchParams]);

  const carregarChecklists = useCallback(() => {
    setLoadingChecklists(true);
    const status = filtro === 'abertos' ? `?status=${STATUS_ABERTOS.join(',')}` : '';
    return Promise.all([
      axios.get(`${API}/insumos/checklists/catalogo`),
      axios.get(`${API}/insumos/checklists${status}`),
      axios.get(`${API}/insumos/checklists?status=finalizado`),
    ])
      .then(([cat, lista, hist]) => {
        setCatalogo(cat.data.data);
        setChecklists(lista.data.data || []);
        setHistorico(hist.data.data || []);
      })
      .catch((err) => setErro(err.response?.data?.message || 'Não foi possível carregar os checklists.'))
      .finally(() => setLoadingChecklists(false));
  }, [filtro]);

  useEffect(() => { carregarChecklists(); }, [carregarChecklists]);

  const abaButton = (chave, label, icon) => (
    <button
      type="button"
      onClick={() => { setAba(chave); setAberto(null); }}
      className={`btn-portal ${aba === chave ? 'btn-portal--primary' : 'btn-portal--outline'}`}
    >
      <i className={`bi ${icon}`} /> {label}
    </button>
  );

  return (
    <div className="content-shell">
      <section className="page-header">
        <div>
          <span className="section-header__eyebrow">TI</span>
          <h1>Insumos</h1>
          <p>Estoque de produtos da TI com extrato de movimentações e checklist de materiais para atendimento em loja.</p>
        </div>
      </section>

      <div className="portal-form-actions" style={{ marginBottom: 16 }}>
        {abaButton('checklists', 'Checklists', 'bi-clipboard-check')}
        {abaButton('estoque', 'Estoque', 'bi-box-seam')}
        {abaButton('extrato', 'Extrato', 'bi-journal-text')}
        {abaButton('ferramentas', 'Ferramentas', 'bi-tools')}
        {abaButton('historico', 'Histórico de atendimentos', 'bi-clock-history')}
      </div>

      {erro ? <div className="feedback-banner feedback-banner--error">{erro}</div> : null}

      {aberto && catalogo ? (
        <ChecklistAtendimento
          key={aberto.id ?? 'novo'}
          checklistId={aberto.id}
          chamadoInicial={aberto.chamado || ''}
          catalogo={catalogo}
          onVoltar={() => setAberto(null)}
          onMudou={carregarChecklists}
        />
      ) : aba === 'checklists' ? (
        <AbaChecklists
          checklists={checklists}
          loading={loadingChecklists}
          filtro={filtro}
          setFiltro={setFiltro}
          onAbrir={(id) => setAberto({ id })}
          onNovo={() => setAberto({ id: null })}
        />
      ) : aba === 'estoque' ? (
        <EstoqueProdutos onAbrirExtrato={() => setAba('extrato')} onMudou={carregarChecklists} />
      ) : aba === 'extrato' ? (
        <AbaExtrato />
      ) : aba === 'ferramentas' ? (
        <PainelFerramentas onMudou={carregarChecklists} />
      ) : (
        <AbaHistorico checklists={historico} loading={loadingChecklists} onAbrir={(id) => setAberto({ id })} />
      )}
    </div>
  );
}
